import { useEffect, useRef } from 'react';
import { useRevalidator } from 'react-router';
import { Logger } from '../utils/Logger';

/**
 * Triggers React Router revalidation when the page becomes visible again.
 *
 * Ensures that route data (logements, etc.) is refreshed every time the user
 * returns to the app from the background, without changing the current route.
 *
 * The revalidation is unconditional (no time threshold): even a brief switch
 * to another app triggers a revalidate. The SW strategies handle deduplication
 * via their sync mutex, so this is safe.
 *
 * Must be used inside a component rendered within RouterProvider.
 */
export function useWakeUpRevalidate(): void {
    const revalidator = useRevalidator();

    // Keep a stable ref to avoid stale closures in the event listener
    const revalidatorRef = useRef(revalidator);
    useEffect(() => {
        revalidatorRef.current = revalidator;
    });

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                Logger.debug('useWakeUpRevalidate', '👁 Page visible - revalidating route data...');
                revalidatorRef.current.revalidate();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []); // empty deps intentional — handler reads from ref
}
